<template>
    <div class="d-flex flex-column" style="height: 100%; min-height: 0">
        <!-- Two siblings: content + footer -->

        <!-- Content -->
        <v-container
            class="flex-grow-1 d-flex flex-column"
            style="min-height: 0; height: 100%"
        >
            <DataFetchLoadingComponent :isLoading="isLoadingFirestore" />

            <!-- Tabs -->
            <v-tabs v-model="tab" color="primary" class="flex-shrink-0">
                <v-tab value="users" width="50%">Users</v-tab>
                <v-tab value="projects" width="50%">
                    <div class="d-flex align-center">
                        <span
                            >Projects
                            <span class="text-primary">{{
                                periodSuffixLabel
                            }}</span></span
                        >
                        <ProjectsPeriodFilterMenu
                            class="ml-2"
                            :projectNames="doProjectMap"
                            :priorityMap="doPriorityMap"
                            :disabled="tab !== 'projects'"
                            @period-changed="handlePeriodChange"
                        />
                    </div>
                </v-tab>
            </v-tabs>

            <!-- Tab Content -->
            <v-tabs-window
                v-model="tab"
                class="flex-grow-1 d-flex flex-column"
                style="min-height: 0; height: 100%; overflow: visible"
            >
                <!-- Users Tab Content -->
                <v-tabs-window-item
                    v-if="tab === 'users'"
                    value="users"
                    class="d-flex flex-column flex-grow-1"
                    style="min-height: 0"
                >
                    <v-row style="min-height: 0">
                        <v-col style="height: 100%">
                            <UsersTable />
                        </v-col>
                    </v-row>
                </v-tabs-window-item>

                <!-- Projects Tab Content -->
                <v-tabs-window-item
                    v-if="tab === 'projects'"
                    value="projects"
                    class="d-flex flex-column flex-grow-1"
                    style="min-height: 0"
                >
                    <v-row style="min-height: 0">
                        <v-col style="height: 100%">
                            <ProjectsTable
                                :tableData="projectsReportData"
                                :selected-period="selectedPeriod"
                                :enableReordering="false"
                                :enablePriority="true"
                                :enableClickNavigation="true"
                                :navigationRoute="'ProjectInfo'"
                                :navigationParam="'projectId'"
                                :currentTab="'projects'"
                            />
                        </v-col>
                    </v-row>
                </v-tabs-window-item>
            </v-tabs-window>

            <!-- Snackbar for Notifications -->
            <v-row class="flex-shrink-0">
                <v-col>
                    <MessageSnackbar
                        :message="snackbar.message"
                        :color="snackbar.color"
                        :timeout="snackbar.timeout"
                        :show="snackbar.show"
                        @update:show="snackbar.show = $event"
                    />
                </v-col>
            </v-row>
        </v-container>

        <!-- Footer - projects tab -->
        <BottomButtonWrapper
            :isShowing="
                tab === 'projects' && (canResetPriorities || canAddNewProject)
            "
        >
            <v-col
                class="d-flex justify-center"
                cols="6"
                v-show="canResetPriorities"
            >
                <v-menu
                    :open-on-hover="!$vuetify.display.smAndDown"
                    location="top"
                >
                    <template v-slot:activator="{ props }">
                        <ThemedActionButton
                            :buttonColor="'error'"
                            :text="' RESET PRIORITY'"
                            v-bind="props"
                        >
                        </ThemedActionButton>
                    </template>

                    <v-list>
                        <v-list-item>
                            <v-btn
                                @click="confirmResetProjectsPriority"
                                variant="flat"
                                class="custom-text-color-error"
                                >PROJECTS PRIORITY
                            </v-btn>
                        </v-list-item>
                        <v-list-item>
                            <v-btn
                                @click="resetProjectPrioritiesForAllUsers"
                                variant="flat"
                                class="custom-text-color-error"
                            >
                                USERS PRIORITY</v-btn
                            ></v-list-item
                        >
                    </v-list>
                </v-menu>
            </v-col>
            <v-col cols="6" v-if="canAddNewProject">
                <AddDialog
                    title="Add New Project"
                    button-text="ADD NEW PROJECT"
                    type="project"
                    :fields="[
                        {
                            key: 'name',
                            label: 'Project Name *',
                            type: 'text',
                            rules: [rules.required],
                        },
                        {
                            key: 'description',
                            label: 'Description *',
                            type: 'text',
                            rules: [rules.required],
                        },
                        {
                            key: 'client',
                            label: 'Client',
                            type: 'text',
                            options: clientOptions,
                        },
                        {
                            key: 'technicalSupport',
                            label: 'Technical Support',
                            type: 'text',
                        },
                        {
                            key: 'teamLeaders',
                            label: 'Team Leaders *',
                            type: 'autocomplete-multiple',
                            options: teamLeaderOptions,
                            required: true,
                            max: 2,
                        },
                        {
                            key: 'isActive',
                            label: 'Status',
                            type: 'select',
                            options: [
                                { title: 'Active', value: true },
                                { title: 'Inactive', value: false },
                            ],
                        },
                    ]"
                />
            </v-col>
        </BottomButtonWrapper>

        <BottomButtonWrapper :isShowing="tab === 'users'">
        </BottomButtonWrapper>
    </div>
</template>

<script>
import UsersTable from "@/components/UsersTable.vue";
import ProjectsTable from "@/components/ProjectsTable.vue";
import { VTabs, VTab } from "vuetify/components";
import ProjectsPeriodFilterMenu from "@/components/ProjectsPeriodFilterMenu.vue";
import DataFetchLoadingComponent from "@/components/DataFetchLoadingComponent.vue";
import BottomButtonWrapper from "@/components/BottomButtonWrapper.vue";
import AddDialog from "@/components/AddDialog.vue";
import ThemedActionButton from "@/components/ThemedActionButton.vue";
import MessageSnackbar from "@/components/MessageSnackbar.vue";
import { mapGetters } from "vuex";
import { adminAndTeamLeadDashboardMixin } from "@/mixins/adminAndTeamLeadDashboardMixin";

export default {
    /**
     * Components used in this view.
     */
    components: {
        UsersTable,
        ProjectsTable,
        VTab,
        VTabs,
        ProjectsPeriodFilterMenu,
        DataFetchLoadingComponent,
        BottomButtonWrapper,
        AddDialog,
        ThemedActionButton,
        MessageSnackbar,
    },
    /**
     * Mixins used in this component.
     */
    mixins: [adminAndTeamLeadDashboardMixin],

    /**
     * Reactive data properties for the component.
     * @returns {Object} An object containing the component's state.
     */
    data() {
        return {
            /**
             * The currently selected tab. Default is 'users'.
             * @type {string}
             */
            tab: "users", // Set default tab to 'users'
            rules: {
                required: (value) => !!value || "Required.",
            },
            // snackbar comes from mixin
        };
    },
    computed: {
        ...mapGetters({
            projects: "getManagedProjects",
            users: "getUsers",
            getRights: "getRights",
            getCompanies: "getCompanies",
        }),
        // periodSuffixLabel, doProjectMap, doPriorityMap come from mixin
        // canAddNewProject, canResetPriorities, teamLeaderOptions, clientOptions come from mixin
    },
    methods: {
        // confirmResetProjectsPriority, resetProjectPrioritiesForAllUsers come from mixin
    },
    // watch comes from mixin
    // mounted comes from mixin
};
</script>
